import React, { useEffect, useState } from "react";
import api from "../../../utils/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion, AnimatePresence } from "framer-motion";
import {
    Download,
    FileText,
    Search,
    CheckCircle2,
    AlertCircle,
    HelpCircle,
} from "lucide-react";
import { downloadApi } from "../../../api/api";
import Footer from "../../../components/footer/Footer";
import Navbar from "../../../components/navbar/Navbar";
import SupportSection from "../section-component/support-section/SupportSection";

const DownloadSection = () => {
    const [downloads, setDownloads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        const fetchDownloads = async () => {
            try {
                const response = await api.get(`${downloadApi}`);
                if (response.data && Array.isArray(response.data)) {
                    setDownloads(response.data);
                } else {
                    toast.error("No downloads found.");
                }
            } catch (error) {
                toast.error("Failed to fetch downloads.");
            } finally {
                setLoading(false);
            }
        };
        fetchDownloads();
    }, []);

    const handleDownload = (filePath, name) => {
        if (!filePath || filePath.length === 0) {
            toast.error("No file available to download.");
            return;
        }
        const link = document.createElement("a");
        link.href = `${filePath[0]}`;
        link.download = name;
        link.target = "_blank";
        link.click();
        toast.success(`Downloading ${name}...`);
    };

    const activeDownloads = downloads.filter(
        (item) => item.status === true || item.status === 1,
    );

    const filteredDownloads = activeDownloads.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    // Strict animation variants matching your established theme
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    return (
        <div className="bg-gray-50 min-h-screen flex flex-col">
            

            {/* --- Hero Section (Dark Vault) --- */}
            <section className="bg-neutral-900 pt-32 pb-32 px-4 sm:px-6 lg:px-8 relative overflow-hidden border-b border-neutral-800">
                {/* Structural Grid Background */}
                <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] [background-size:40px_40px]"></div>

                <div className="max-w-7xl mx-auto text-center relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                            Download{" "}
                            <span className="text-yellow-500">Center.</span>
                        </h1>
                        <p className="text-gray-400 text-lg max-w-2xl mx-auto font-medium leading-relaxed">
                            Your centralized repository for verified study
                            materials, high-yield PDFs, and crucial exam
                            resources.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* --- Overlapping Info Cards --- */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-20 w-full mb-12">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                        {
                            icon: <CheckCircle2 size={24} />,
                            title: "Verified Files",
                            desc: "All materials are thoroughly checked by expert mentors.",
                        },
                        {
                            icon: <Download size={24} />,
                            title: "Instant Access",
                            desc: "One-click downloads straight to your device.",
                        },
                        {
                            icon: <HelpCircle size={24} />,
                            title: "24/7 Support",
                            desc: "Having trouble? Reach out to our technical helpdesk.",
                        },
                    ].map((info, idx) => (
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                delay: 0.2 + idx * 0.1,
                                duration: 0.4,
                            }}
                            className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow flex items-start gap-5"
                        >
                            <div className="w-12 h-12 bg-yellow-400 text-neutral-900 rounded-lg flex items-center justify-center shrink-0 shadow-sm">
                                {info.icon}
                            </div>
                            <div>
                                <h3 className="text-lg font-black text-neutral-900 mb-1">
                                    {info.title}
                                </h3>
                                <p className="text-sm text-gray-500 font-medium leading-relaxed">
                                    {info.desc}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* --- Main Content Area --- */}
            <section className="flex-1 py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
                {/* Standardized Search Bar */}
                <div className="mb-12 relative max-w-2xl mx-auto">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Search className="text-gray-400" size={20} />
                    </div>
                    <input
                        type="text"
                        placeholder="Search for PDFs, syllabus, or notes..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-white border border-gray-200 text-neutral-900 text-base py-4 pl-12 pr-6 rounded-xl shadow-sm focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all font-medium placeholder:text-gray-400"
                    />
                </div>

                {/* Downloads Grid */}
                {loading ? (
                    /* Skeleton Loader */
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {[...Array(8)].map((_, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm animate-pulse flex flex-col justify-between h-[200px]"
                            >
                                <div className="flex gap-4 mb-6">
                                    <div className="w-12 h-12 bg-gray-100 rounded-lg shrink-0"></div>
                                    <div className="flex-1 pt-1">
                                        <div className="h-5 bg-gray-100 rounded w-3/4 mb-2"></div>
                                        <div className="h-4 bg-gray-50 rounded w-1/2"></div>
                                    </div>
                                </div>
                                <div className="mt-auto h-12 bg-gray-50 rounded-lg w-full border border-gray-100"></div>
                            </div>
                        ))}
                    </div>
                ) : filteredDownloads.length === 0 ? (
                    /* Empty State */
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center max-w-2xl mx-auto"
                    >
                        <div className="w-16 h-16 bg-gray-50 border border-gray-100 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertCircle size={28} />
                        </div>
                        <h3 className="text-xl font-bold text-neutral-900 mb-2">
                            No files found
                        </h3>
                        <p className="text-gray-500 font-medium text-sm">
                            We couldn't find any resources matching "
                            {searchTerm}". Try a different keyword or check back
                            later.
                        </p>
                    </motion.div>
                ) : (
                    /* Document Grid */
                    <motion.div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <AnimatePresence>
                            {filteredDownloads.map((item) => (
                                <motion.div
                                    key={item.id}
                                    variants={itemVariants}
                                    layout
                                    onClick={() =>
                                        handleDownload(
                                            item.file_path,
                                            item.name,
                                        )
                                    }
                                    className="group relative bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-neutral-900 transition-all duration-300 cursor-pointer flex flex-col overflow-hidden h-full"
                                >
                                    {/* Signature Top Accent Line */}
                                    <div className="absolute top-0 left-0 w-full h-[3px] bg-yellow-400 scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500 ease-out z-10"></div>

                                    <div className="p-6 flex flex-col flex-1">
                                        <div className="flex items-start gap-4 mb-5">
                                            {/* Icon Container */}
                                            <div className="w-12 h-12  group-hover:bg-yellow-400 text-neutral-900 rounded-lg flex items-center justify-center shrink-0 group-hover:border-yellow-400 transition-colors">
                                                <FileText size={20} />
                                            </div>

                                            {/* Title & Badge */}
                                            <div className="flex-1 pt-0.5">
                                                <h3 className="font-bold text-neutral-900 text-base group-hover:text-red-700 transition-colors">
                                                    {item.name}
                                                </h3>
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1.5">
                                                    PDF Document
                                                </p>
                                            </div>
                                        </div>

                                        {/* Action Button */}
                                        <div className="mt-auto pt-4 border-t border-gray-100">
                                            <div className="w-full py-2.5 px-4 bg-neutral-50 border border-gray-200 rounded-lg flex items-center justify-between text-sm font-bold text-neutral-900 group-hover:bg-neutral-900 group-hover:text-white group-hover:border-neutral-900 transition-all duration-300">
                                                <span>Download File</span>
                                                <Download
                                                    size={16}
                                                    className="text-gray-400 group-hover:text-white group-hover:translate-y-0.5 transition-all"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </motion.div>
                )}
            </section>

            <SupportSection />
            
        </div>
    );
};

export default DownloadSection;
